<!--
  @fileoverview Radar Chart Component
  @module shared-ui/components/RadarChart

  Specialized radar chart for learning profiles and multi-dimensional data
  Built on top of BaseChart component
-->

<template>
	<BaseChart
		type="radar"
		:data="chartData"
		:options="chartOptions"
		:width="props.size"
		:height="props.size"
		:aria-label="props.ariaLabel"
		:maintain-aspect-ratio="true"
		@chart-ready="handleChartReady"
	/>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BaseChart from './BaseChart.vue';
import type { ChartData, ChartOptions } from 'chart.js';
import type { RadarChartProps, DatasetConfig } from '@monorepo/shared-ui';

const props = withDefaults(defineProps<RadarChartProps>(), {
	labels: undefined,
	datasets: undefined,
	maxScore: 100,
	size: 400,
	showLegend: true,
	ariaLabel: 'Radar Chart',
	gridColor: 'rgba(0, 0, 0, 0.08)',
	angleLineColor: 'rgba(0, 0, 0, 0.1)',
});

const emit = defineEmits<{
	'chart-ready': [chart: any];
}>();

/**
 * Prepare chart data from scores
 */
const chartData = computed((): ChartData<'radar'> => {
	// Prepare labels
	const labels = props.labels || Object.keys(props.scores);

	// Prepare datasets
	let datasets: DatasetConfig[];
	if (props.datasets && props.datasets.length > 0) {
		datasets = props.datasets;
	} else {
		// Create single dataset from scores
		const data = Object.values(props.scores);
		datasets = [
			{
				label: 'Scores',
				data: data,
				borderColor: 'rgba(59, 130, 246, 1)', // blue-500
				backgroundColor: 'rgba(59, 130, 246, 0.25)',
				pointBackgroundColor: 'rgba(59, 130, 246, 1)',
				borderWidth: 3,
				pointRadius: 5,
				pointHoverRadius: 7,
				pointBorderColor: '#fff',
				pointBorderWidth: 2,
			},
		];
	}

	return {
		labels,
		datasets: datasets.map((dataset) => ({
			label: dataset.label,
			data: dataset.data,
			borderColor: dataset.borderColor || 'rgba(59, 130, 246, 1)',
			backgroundColor: dataset.backgroundColor || 'rgba(59, 130, 246, 0.25)',
			borderWidth: dataset.borderWidth || 3,
			pointBackgroundColor:
				dataset.pointBackgroundColor || dataset.borderColor || 'rgba(59, 130, 246, 1)',
			pointBorderColor: dataset.pointBorderColor || '#fff',
			pointRadius: dataset.pointRadius || 5,
			pointHoverRadius: dataset.pointHoverRadius || 7,
			pointBorderWidth: dataset.pointBorderWidth || 2,
		})),
	};
});

/**
 * Chart options for radar chart
 */
const chartOptions = computed((): ChartOptions<'radar'> => ({
	responsive: true,
	maintainAspectRatio: true,
	plugins: {
		legend: {
			display: props.showLegend,
			position: 'top',
			labels: {
				font: {
					size: 14,
				},
				padding: 15,
				usePointStyle: true,
			},
		},
		tooltip: {
			enabled: true,
			backgroundColor: 'rgba(0, 0, 0, 0.8)',
			padding: 12,
			titleFont: {
				size: 14,
			},
			bodyFont: {
				size: 13,
			},
		},
	},
	scales: {
		r: {
			beginAtZero: true,
			min: 0,
			max: props.maxScore,
			angleLines: {
				color: props.angleLineColor,
				lineWidth: 1,
			},
			grid: {
				color: props.gridColor,
				lineWidth: 1,
			},
			pointLabels: {
				font: {
					size: 14,
					weight: 'bold',
				},
				color: '#374151', // gray-700
			},
			ticks: {
				showLabelBackdrop: false,
				color: '#6b7280', // gray-500
				font: {
					size: 11,
				},
				stepSize: props.maxScore / 5,
			},
		},
	},
	elements: {
		line: {
			tension: 0.3, // Smooth curves
		},
	},
}));

function handleChartReady(chart: any): void {
	emit('chart-ready', chart);
}
</script>
